import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import {
  SsoQueryParamsTypes,
  SsoTypeTypes,
} from '~/components/SSO/utils/types';
import SSOLink from '~/components/SSOLink';
import useResponsive from '~/hooks/useResponsive';

// ============================================================================
// Type Definitions
// ============================================================================
interface ResponsiveActionCardV2Props {
  /** Card title - can be string or custom React node */
  title: string | ReactNode;
  /** Optional icon to display next to title */
  icon?: ReactNode;
  /** Test ID for testing purposes */
  dataTestId?: string;
  /** Card background color */
  backgroundColor?: string;
  /** Label for the primary action button */
  buttonLabel?: string;
  /** Callback for button click events */
  onButtonClick?: () => void;
  /** Card body content */
  children: ReactNode;
  /** Optional action menu (kebab menu, etc.) */
  actionMenu?: ReactNode;
  /** Loading state for the action button */
  isButtonLoading?: boolean;
  /** Disabled state for the action button */
  isButtonDisabled?: boolean;
  /** Custom action button to override default */
  actionButton?: ReactNode;
  /** Direct href link for button */
  href?: string;
  /** SSO link type */
  type?: string;
  /** SSO query parameters */
  queryParams?: SsoQueryParamsTypes;
  /** Optional alert banner for restore functionality */
  alertBannerForRestore?: ReactNode;
}

// ============================================================================
// Styled Components
// ============================================================================
const StyledCard = styled(Card)(() => ({
  // Keep the header action within the card padding so it lines up with the
  // content and the primary action button below it.
  '& .MuiCardHeader-action': {
    margin: 0,
    alignSelf: 'center',
  },
  '& .MuiCardContent-root': {
    paddingRight: '10px',
  },
}));

const StyledCardActions = styled(CardActions)(({ theme }) => ({
  marginTop: 'auto',
  padding: 0,
  display: 'flex',
  justifyContent: 'flex-start',
  flexShrink: 0,
}));

// ============================================================================
// Helper Components
// ============================================================================
interface ActionButtonProps {
  actionButton?: ReactNode;
  href?: string;
  type?: string;
  queryParams?: SsoQueryParamsTypes;
  onButtonClick?: () => void;
  buttonLabel: string;
  isButtonLoading: boolean;
  isButtonDisabled: boolean;
  dataTestId: string;
  fullWidth?: boolean;
}

/** Renders the appropriate action button based on props */
const ActionButton = ({
  actionButton = null,
  href = undefined,
  type = undefined,
  queryParams = undefined,
  onButtonClick = undefined,
  buttonLabel,
  isButtonLoading,
  isButtonDisabled,
  dataTestId,
  fullWidth = false,
}: ActionButtonProps) => {
  // Custom action button takes precedence
  if (actionButton) {
    return <>{actionButton}</>;
  }

  // Direct href link
  if (href) {
    return (
      <Button
        variant="outlined"
        href={href}
        color="primary"
        fullWidth={fullWidth}
      >
        {buttonLabel}
      </Button>
    );
  }

  // SSO link
  if (type && queryParams) {
    return (
      <SSOLink
        type={type as SsoTypeTypes}
        queryParams={queryParams}
        variant="outlined"
        data-testid={`${dataTestId}-sso-link`}
      >
        {buttonLabel}
      </SSOLink>
    );
  }

  // Standard button with click handler
  if (onButtonClick) {
    return (
      <LoadingButton
        variant="outlined"
        loading={isButtonLoading}
        disabled={isButtonDisabled}
        onClick={onButtonClick}
        data-testid={`${dataTestId}-button`}
        fullWidth={fullWidth}
      >
        {buttonLabel}
      </LoadingButton>
    );
  }

  return null;
};

/** Renders the card title with optional icon */
const CardTitle = ({
  icon = null,
  title,
}: {
  icon?: ReactNode;
  title: string | ReactNode;
}) => (
  <Stack direction="row" spacing={1} alignItems="center">
    {icon && <Box>{icon}</Box>}
    <Typography variant="h3">{title}</Typography>
  </Stack>
);

// ============================================================================
// Main Component
// ============================================================================
const ResponsiveActionCardV2 = ({
  title,
  icon = null,
  buttonLabel = '',
  onButtonClick = undefined,
  children,
  isButtonLoading = false,
  isButtonDisabled = false,
  actionMenu = null,
  actionButton = null,
  href = undefined,
  type = undefined,
  queryParams = undefined,
  backgroundColor = '',
  dataTestId = 'responsive-action-card',
  alertBannerForRestore = null,
}: ResponsiveActionCardV2Props) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'responsiveActionCard',
  });

  const { isMobile } = useResponsive();

  // Determine the final button label
  const finalButtonLabel = buttonLabel || t('buttonLabel');

  // Common action button props
  const actionButtonProps = {
    actionButton,
    href,
    type,
    queryParams,
    onButtonClick,
    buttonLabel: finalButtonLabel,
    isButtonLoading,
    isButtonDisabled,
    dataTestId,
  };

  // Common card styles
  const cardStyles = {
    backgroundColor,
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
  };

  // ============================================================================
  // Mobile Layout
  // ============================================================================
  if (isMobile()) {
    return (
      <StyledCard data-testid={dataTestId} sx={cardStyles}>
        {/* Alert banner for restore functionality */}
        {alertBannerForRestore}

        {/* Card header with title and action menu */}
        <CardHeader
          title={<CardTitle icon={icon} title={title} />}
          action={actionMenu}
        />

        {/* Card content body */}
        <CardContent>
          <Box sx={{ pb: 2 }}>{children}</Box>
        </CardContent>

        {/* Action button section */}
        <Stack pb={2} pl={2} pr={2}>
          <ActionButton {...actionButtonProps} fullWidth />
        </Stack>
      </StyledCard>
    );
  }

  // ============================================================================
  // Desktop Layout
  // ============================================================================
  return (
    <StyledCard data-testid={dataTestId} sx={{ ...cardStyles, p: 3 }}>
      {/* Alert banner for restore functionality */}
      {alertBannerForRestore}

      {/* Card header with title and optional action (kebab / refresh / etc.) */}
      <CardHeader
        title={<CardTitle icon={icon} title={title} />}
        action={actionMenu}
        sx={{ p: 0, mb: 2 }}
      />

      {/* Card content body */}
      <CardContent sx={{ p: 0 }}>{children}</CardContent>

      {/* Primary action button — independent of header actionMenu */}
      <StyledCardActions>
        <ActionButton {...actionButtonProps} fullWidth />
      </StyledCardActions>
    </StyledCard>
  );
};

export default ResponsiveActionCardV2;
